import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import WhotCard from '../whot/components/WhotCard';
import { CARD_SHAPES, SPECIAL_CARDS } from '../whot/utils/constants';
import { createOrderedCards } from '../whot/utils/deck';
import '../styles/Whot.css';

const SHAPE_ORDER = [
  CARD_SHAPES.CIRCLE,
  CARD_SHAPES.TRIANGLE,
  CARD_SHAPES.SQUARE,
  CARD_SHAPES.CROSS,
  CARD_SHAPES.STAR,
];

const shapeLabel = (shape) => {
  switch (shape) {
    case CARD_SHAPES.CIRCLE:
      return 'CIRCLE';
    case CARD_SHAPES.TRIANGLE:
      return 'TRIANGLE';
    case CARD_SHAPES.SQUARE:
      return 'SQUARE';
    case CARD_SHAPES.CROSS:
      return 'CROSS';
    case CARD_SHAPES.STAR:
      return 'STAR';
    default:
      return String(shape);
  }
};

export default function WhotCardsPreview() {
  const navigate = useNavigate();
  const [isSmall, setIsSmall] = useState(false);
  const [showFaceDown, setShowFaceDown] = useState(true);

  const cards = useMemo(() => createOrderedCards(), []);

  const grouped = useMemo(() => {
    const byShape = new Map();
    SHAPE_ORDER.forEach((s) => byShape.set(s, []));

    (cards || []).forEach((c) => {
      if (c.number === SPECIAL_CARDS.WHOT.number) return;
      const arr = byShape.get(c.shape) || [];
      arr.push(c);
      byShape.set(c.shape, arr);
    });

    // Keep them ordered by number
    for (const [shape, arr] of byShape.entries()) {
      arr.sort((a, b) => Number(a.number) - Number(b.number));
      byShape.set(shape, arr);
    }

    return byShape;
  }, [cards]);

  const whotCards = useMemo(
    () => (cards || []).filter((c) => c.number === SPECIAL_CARDS.WHOT.number),
    [cards]
  );

  return (
    <div className="whot-page">
      <div className="whot-shell">
        <div className="whot-header">
          <div>
            <h1 className="whot-title" style={{ marginBottom: 0 }}>WHOT Card Preview</h1>
            <div className="whot-subtitle">Quick page to review every card design</div>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button className="whot-btn" onClick={() => navigate('/whot-online')}>← Back</button>
          </div>
        </div>

        <div className="whot-card">
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <button
              className={isSmall ? 'whot-btn' : 'whot-btn whot-btn-primary'}
              onClick={() => setIsSmall(false)}
            >
              Normal size
            </button>
            <button
              className={isSmall ? 'whot-btn whot-btn-primary' : 'whot-btn'}
              onClick={() => setIsSmall(true)}
            >
              Small size
            </button>

            <label className="whot-pill" style={{ cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={showFaceDown}
                onChange={(e) => setShowFaceDown(e.target.checked)}
                style={{ marginRight: 8 }}
              />
              Show face-down
            </label>
          </div>

          {showFaceDown && (
            <div style={{ marginTop: 14 }}>
              <div className="whot-muted" style={{ marginBottom: 8 }}>Face-down (deck)</div>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <WhotCard isFaceDown badge={54} isSmall={isSmall} onClick={() => {}} />
                <WhotCard isFaceDown badge={12} isSmall={isSmall} onClick={() => {}} />
              </div>
            </div>
          )}

          <div style={{ marginTop: 16 }}>
            <div className="whot-muted" style={{ marginBottom: 8 }}>WHOT (20)</div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {whotCards.map((c) => (
                <WhotCard key={c.id} card={c} isPlayable={false} isSmall={isSmall} />
              ))}
            </div>
          </div>
        </div>

        {SHAPE_ORDER.map((shape) => {
          const list = grouped.get(shape) || [];
          if (!list.length) return null;
          return (
            <div key={shape} className="whot-card" style={{ marginTop: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <h2 style={{ margin: 0 }}>{shapeLabel(shape)}</h2>
                <div className="whot-pill">{list.length} cards</div>
              </div>

              <div className="whot-preview-grid">
                {list.map((c) => (
                  <div key={c.id} className="whot-preview-item">
                    <WhotCard card={c} isPlayable={false} isSmall={isSmall} />
                    <div className="whot-preview-caption">{c.number} {c.shape}</div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
